import { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Card, Space, Input, InputNumber, Switch } from "antd";
import { BellOutlined, NotificationOutlined, CloseOutlined } from "@ant-design/icons";
import { addNotification } from "../../store/notifications.slice";
import "./NotificationTester.scss";

const { TextArea } = Input;

// Sample notification data from backend
const SAMPLE_NOTIFICATION = {
  type: "order",
  subType: "incoming",
  orderType: "1",
  orderId: "49214",
  click_action: "FLUTTER_NOTIFICATION_CLICK",
  shopId: "jzrnh83baPySa4CT1NBV",
  externalOrderId: "direct-dukaan:D560027G004-27012026-035",
  customerName: "vijai chander",
  customerPhone: "9884044494",
  orderTotal: "2874.00",
  deepLink:
    "https://shopeg.in/devapp/incomingDukaanOrder?o=49214&e=direct-dukaan:D560027G004-27012026-035&sid=jzrnh83baPySa4CT1NBV",
  customerId: "DKQu3kptQpNICI7R5QDIebye8qx2",
  newOrdersCount: "192",
  open_order_count: "310",
  title: "#27012026-035 New Online Order 🎉",
  body: "<< View details and prepare for delivery >>",
};

const NotificationTester = () => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [title, setTitle] = useState(SAMPLE_NOTIFICATION.title);
  const [body, setBody] = useState(SAMPLE_NOTIFICATION.body);
  const [notificationData, setNotificationData] = useState(
    JSON.stringify(SAMPLE_NOTIFICATION, null, 2)
  );
  const [stackCount, setStackCount] = useState(3);
  const [includeDetails, setIncludeDetails] = useState(true);

  const sendNotification = () => {
    try {
      const data = JSON.parse(notificationData);
      dispatch(
        addNotification({
          title,
          body,
          data,
        })
      );
    } catch (error) {
      console.error("Invalid JSON in notification data:", error);
      alert("Invalid JSON format in notification data");
    }
  };

  const sendStackedNotifications = () => {
    try {
      const baseData = JSON.parse(notificationData);

      for (let i = 0; i < stackCount; i++) {
        const orderNumber = `#${Date.now()}-${i}`;
        const orderId = String(49214 + i);
        const externalOrderId = `direct-dukaan:D560027G004-${Date.now()}-${String(i).padStart(3, "0")}`;

        const data = {
          ...baseData,
          orderId,
          externalOrderId,
        };

        // Remove customer details if not included
        if (!includeDetails) {
          delete data.customerName;
          delete data.customerPhone;
          delete data.orderTotal;
        }

        dispatch(
          addNotification({
            title: `${orderNumber} New Online Order 🎉`,
            body: "<< View details and prepare for delivery >>",
            data,
          })
        );

        // Small delay between notifications to ensure they stack properly
        if (i < stackCount - 1) {
          setTimeout(() => {}, 100);
        }
      }
    } catch (error) {
      console.error("Invalid JSON in notification data:", error);
      alert("Invalid JSON format in notification data");
    }
  };

  const resetToSample = () => {
    setTitle(SAMPLE_NOTIFICATION.title);
    setBody(SAMPLE_NOTIFICATION.body);
    setNotificationData(JSON.stringify(SAMPLE_NOTIFICATION, null, 2));
  };

  // Only show in localdev mode
  if (import.meta.env.MODE !== "localdev") {
    return null;
  }

  return (
    <div className="notification-tester">
      {/* Floating toggle button */}
      {!isExpanded && (
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<BellOutlined />}
          onClick={() => setIsExpanded(true)}
          className="notification-tester-toggle"
          title="Open Notification Tester"
        />
      )}

      {/* Expanded panel */}
      {isExpanded && (
        <Card
          title={
            <Space>
              <BellOutlined />
              Notification Tester
            </Space>
          }
          className="notification-tester-card"
          extra={
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setIsExpanded(false)}
              style={{ color: "white" }}
            />
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
          <div>
            <label className="notification-tester-label">Title:</label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Notification title"
            />
          </div>

          <div>
            <label className="notification-tester-label">Body:</label>
            <Input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Notification body"
            />
          </div>

          <div>
            <label className="notification-tester-label">
              Notification Data (JSON):
            </label>
            <TextArea
              value={notificationData}
              onChange={(e) => setNotificationData(e.target.value)}
              placeholder="Notification data as JSON"
              rows={10}
              style={{ fontFamily: "monospace", fontSize: "0.75rem" }}
            />
          </div>

          <Space wrap>
            <Button
              type="primary"
              icon={<NotificationOutlined />}
              onClick={sendNotification}
            >
              Send Single Notification
            </Button>

            <Button onClick={resetToSample}>Reset to Sample</Button>
          </Space>

          <div className="notification-tester-divider" />

          <div className="notification-tester-stack-section">
            <h4>Test Stacked Notifications</h4>
            <Space direction="vertical" style={{ width: "100%" }}>
              <div>
                <label className="notification-tester-label">
                  Number of notifications:
                </label>
                <InputNumber
                  min={2}
                  max={10}
                  value={stackCount}
                  onChange={(value) => setStackCount(value || 3)}
                  style={{ width: "100%" }}
                />
              </div>

              <div>
                <Space>
                  <Switch
                    checked={includeDetails}
                    onChange={setIncludeDetails}
                  />
                  <span>Include customer details (name, phone, total)</span>
                </Space>
              </div>

              <Button
                type="primary"
                icon={<NotificationOutlined />}
                onClick={sendStackedNotifications}
                block
              >
                Send {stackCount} Stacked Notifications
              </Button>
            </Space>
          </div>
        </Space>
      </Card>
      )}
    </div>
  );
};

export default NotificationTester;
